/**
 * LiveE Event Set
 */

import { has } from '../../../app/helpers/object'

/**
 * LiveEventSetFactory
 * returns LiveEventSet entity class
 *
 * @class LiveEventSetFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.LiveEventSet} class
 *
 * @ngdoc factory
 * @name mwApp.factory:LiveEventSetFactory
 */
/* @ngInject */
export default function LiveEventSetFactory ($http, $injector, Entity, FileUploadMixin, alertLiteSvc) {
  /* === LiveEventSet class === */

  /**
   * LiveEventSet model
   *
   * @class LiveEventSet
   * @classdesc LiveEventSet model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @mixes models.FileUploadMixin
   * @return {models.LiveEventSet} LiveEventSet entity
   */
  function LiveEventSet () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }

  LiveEventSet.prototype = Object.create(Entity.prototype)
  LiveEventSet.prototype.constructor = LiveEventSet

  /* === LiveEventSet instance methods & fields === */

  Object.assign(LiveEventSet.prototype, {

    /**
     * instance fields
     *
     * @memberof models.LiveEventSet#
     * @type {Object}
     */
    _fields: {
      id: Number,
      assignedSubscriptions: ['Subscription'],
      contentItems: [],
      coverImageUrl: String,
      description: String,
      enabled: true,
      exclusivePaymentSystemSetting: {
        id: Number,
        assignedPaymentSystems: [{ paymentSystemId: Number, paymentSystemType: String }]
      },
      liveEventIdsToAssign: [],
      liveEventIdsToRemove: [],
      name: String,
      networkId: Number,
      numberOfCcu: Number,
      pricing: {
        id: Number,
        buy: {
          paymentMethod: String,
          amount: Number
        },
        currency: 'Currency'
      },
      richDescription: String,
      quantity: Number,
      updatedAt: String,
      updatedBy: String,
      $buyEnabled: true,
      $itemType: 'live-events',
      $overrideCCU: Boolean,
      $setType: 'live_event'
    },

    /**
     * url template
     *
     * @memberof models.LiveEventSet#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/live-event-sets/{{::id}}',

    /**
     * url template for available channel sets
     *
     * @memberof models.LiveEventSet#
     * @type {String}
     */
    _availableLiveEventSetsTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/live-event-sets?available',

    /**
     * public url template
     *
     * @memberof models.LiveEventSet#
     * @type {String}
     */
    _publicUrlTemplate: 'organizations/{{::networkId}}/contentSets/liveEvent/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.LiveEventSet#
     * @type {String}
     */
    _subscriptionUrlTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/live-event-sets',

    /**
     * url template for checking set intersection
     *
     * @memberof models.LiveEventSet#
     * @type {String}
     */
    _contentIntersectionUrlTemplate: 'api/networks/{{::networkId}}/live-event-sets/{{::id}}/intersection',

    /**
     * url template for loading custom prices
     *
     * @memberof models.LiveEventSet#
     * @type {String}
     */
    _customPricesUrlTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/live-event-sets/{{::setId}}/prices',

    /**
     * Load liveEvents extra for Live Event set
     *
     * @memberof models.LiveEventSet#
     * @returns {Promise} promise
     */
    _getAvailableLiveEvents: function (networkId, params = {}) {
      const extraUrl = `api/networks/${networkId}/live-event-sets/${this.id || 0}/live-events?available`
      return $http.get(extraUrl, { params })
        .data()
        .then((response) => response.payload)
        .catch(rejection => alertLiteSvc.alertError(rejection))
    },
    /**
     * Load channels extra for Channel set
     *
     * @memberof models.LiveEventSet#
     * @returns {Promise} promise
     */
    _getAssignedLiveEvents: function (networkId, params) {
      if (params.q) {
        params['live-event-name'] = params.q
        delete params.q
      }

      const extraUrl = `api/networks/${networkId}/live-event-sets/${this.id || 0}/live-events?assigned`
      return $http.get(extraUrl, { params })
        .data()
        .then((response) => {
          return response.payload
        })
        .catch(rejection => alertLiteSvc.alertError(rejection))
    },

    /**
     * Prepare object
     *
     * @memberof models.LiveEventSet#
     * @override
     */
    _prepare: function () {
      this.$buyEnabled = true
      if (!this.pricing) {
        this.pricing = {
          buy: {
            paymentMethod: 'PER_ITEM',
            amount: 0
          }
        }
      }
    },

    /**
     * Get underlying object
     *
     * @memberof models.LiveEventSet#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      return Entity.prototype._object.apply(this, arguments)
    },

    /**
     * Patch method
     *
     * @memberof models.LiveEvent
     * @param {String} url
     * @param params
     * @return {Promise} promise
     * @private
     */
    _patch: function (url, params) {
      return $http.patch(url, params)
        .then(
          response => {
            if (response.data) {
              this._weakCopy(response.data)
            }
            return response
          }
        ).catch(
          rejection => {
            return alertLiteSvc.alertError(rejection)
          })
    },
    /**
     * On/Off Entity, performing PATCH request to the server
     *
     * @memberof models.LiveEvent
     * @param {Boolean} [enabled=this.enabled] - enable or disable Entity
     * @return {Promise} promise
     */
    _onOff: function (enabled) {
      enabled = this.enabled

      return this._patch(this._url, { enabled })
        .catch(rejection => {
          this.enabled = !this.enabled
          return rejection
        })
    }
  }, FileUploadMixin)

  /* === LiveEventSet static methods & fields === */

  /**
   * Generate url
   *
   * @param uri
   * @param {Object} [o] - object over which Url will be interpolated
   * @return {String}
   */
  LiveEventSet.generateUrl = function (uri = '', o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    let url = cast.prototype.$_urlTemplate(o)
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url
    return url + uri
  }

  /**
   * Get channel sets by params
   *
   * @memberof models.LiveEventSet#
   */
  LiveEventSet.loadSmartLiveEventSets = function (tableState, o) {
    o = o || {}

    var url = LiveEventSet.generateUrl('', o)

    const params = {
      'sort-by': tableState.sort.predicate ? tableState.sort.predicate : '',
      'sort-order': tableState.sort.reverse === false ? 'asc' : 'desc',
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number
    }
    Object.assign(params, tableState.search.predicateObject)
    if (params.$) {
      params.q = params.$
      delete params.$
    }
    if (params.updatedBy) {
      params['updated-by'] = params.updatedBy
      delete params.updatedBy
    }
    if (o.searchLiveEvent) {
      params['live-event-id'] = o.searchLiveEvent.id
    }
    return $http.get(url, { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          var result = this.cast(data, {
            // response structure
            content: [this._name],
            first: true,
            last: true,
            number: 0,
            numberOfElements: 0,
            size: 0,
            sort: [{
              ascending: true,
              direction: '',
              ignoreCase: true,
              nullHandling: '',
              property: ''
            }],
            totalElements: 0,
            totalPages: 0
          })

          // change SmartTable state
          if (Array.isArray(result.sort) && result.sort.length) {
            var last = result.sort.pop()
            tableState.sort.predicate = last.property
            tableState.sort.reverse = !last.ascending
          }
          tableState.pagination.start = result.number * result.size // first element
          tableState.pagination.number = result.size // page size
          tableState.pagination.numberOfPages = result.totalPages
          tableState.pagination.totalItemCount = result.totalElements

          // return array for SmartTable
          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  /**
   * Get assigned shows
   *
   * @memberof models.ChannelSet#
   */
  LiveEventSet.getAssignedLiveEvents = function (tableState, networkId) {
    const params = {
      page: tableState.page || 0,
      count: 25
    }

    if (Object.prototype.hasOwnProperty.call(tableState, 'q')) {
      params['live-event-name'] = tableState.q
    }

    const url = `/nora/api/networks/${networkId}/live-event-sets/live-events?assigned`

    return $http.get(url, { params })
      .data()
      .then((response) => response.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  LiveEventSet.loadLiveEventSets = function (tableState, o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    const url = cast.prototype.$_availableLiveEventSetsTemplate(o)

    return $http.get(url)
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          var result = this.cast(data, {
            // response structure
            content: [cast._name],
            last: true,
            number: 0,
            size: 0,
            totalElements: 0,
            totalPages: 0
          })
          if (result) {
            tableState.page = result.number
            tableState.count = result.size // page size
          }

          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  // copy all static methods from Entity class
  Object.assign(LiveEventSet, Entity)
  LiveEventSet._name = 'LiveEventSet'
  LiveEventSet._publicName = 'Live Event Set'
  LiveEventSet.init()

  // permissions
  LiveEventSet.access = {
    view: '',
    create: '',
    edit: '',
    delete: ''
  }

  return LiveEventSet
}
